// shared/utils/request.ts
import axios, { type AxiosRequestConfig } from 'axios';
import { URLS } from '@/shared/config/urls';
import { useAuthStore } from '@/store/authStore';

const api = axios.create({
  baseURL: URLS.BACK_DSH,
  // timeout: 20000,
  withCredentials: true, // Automatic cookie transmission
});

// Request interceptor - No additional logic needed as cookies are sent automatically
api.interceptors.request.use(
  (config) => {
    // In cookie-based authentication, server automatically checks session cookies
    // Client only needs to set credentials: 'include'
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Authentication error handling
api.interceptors.response.use(
  (response) => response.data,
  (error) => {
    // 401 = no valid session: sign out locally. 403 = signed in but not
    // allowed: keep the session and let the caller show the error.
    if (error.response?.status === 401) {
      useAuthStore.getState().clearAuth();
    }

    // Network error handling
    if (error.code === 'NETWORK_ERROR') {
      console.error('Please check your network connection.');
    }

    // Extract the backend error message (e.g. "msg > password mismatch") so that
    // callers can use `t(error.message)` to display a translated message instead of
    // the generic axios "Request failed with status code 400".
    const serverMessage = error.response?.data?.message;
    if (serverMessage) {
      error.message = serverMessage;
    }

    return Promise.reject(error);
  }
);

// API request functions
export const request = {
  get: <T>(url: string, config?: AxiosRequestConfig): Promise<T> =>
    api.get(url, config) as Promise<T>,
  post: <T>(url: string, data?: unknown, config?: AxiosRequestConfig | boolean): Promise<T> =>
    api.post(url, data, config as AxiosRequestConfig) as Promise<T>,
  put: <T>(url: string, data?: unknown, config?: AxiosRequestConfig | boolean): Promise<T> =>
    api.put(url, data, config as AxiosRequestConfig) as Promise<T>,
  patch: <T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> =>
    api.patch(url, data, config) as Promise<T>,
  delete: <T>(url: string, config?: AxiosRequestConfig | string | number): Promise<T> =>
    api.delete(url, typeof config === 'object' ? config : undefined) as Promise<T>,
};
